import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Form, InputGroup, Card, Spinner } from 'react-bootstrap';
import axios from 'axios';
import GLOBALVAR from '../../constants';
import AddTemplate from './AddTemplate';

const TemplateList = ({ showNotification }) => {
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('');
    const [category, setCategory] = useState('');
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState(null);

    const fetchTemplates = useCallback(async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${GLOBALVAR['SERVER_ROOT']}/api/templates${category ? `?category=${category}` : ''}`);
            setTemplates(response.data);
        } catch (error) {
            showNotification(error.response?.data?.message || 'Failed to fetch templates', 'danger');
        } finally {
            setLoading(false);
        }
    }, [category, showNotification]);

    useEffect(() => {
        fetchTemplates();
    }, [category, fetchTemplates]);

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this template?')) {
            try {
                await axios.delete(`${GLOBALVAR['SERVER_ROOT']}/api/templates/${id}`);
                showNotification('Template deleted successfully');
                fetchTemplates();
            } catch (error) {
                showNotification(error.response?.data?.message || 'Failed to delete template', 'danger');
            }
        }
    };

    const handleEdit = (template) => {
        setSelectedTemplate(template);
        setShowEditModal(true);
    };

    const filteredTemplates = templates.filter(template => 
        template.name.toLowerCase().includes(filter.toLowerCase()) ||
        template.category.toLowerCase().includes(filter.toLowerCase())
    );

    return (
        <Card>
            <Card.Body>
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <InputGroup className="w-50">
                        <InputGroup.Text>
                            <i className="fas fa-search"></i>
                        </InputGroup.Text>
                        <Form.Control
                            placeholder="Search templates..."
                            value={filter}
                            onChange={(e) => setFilter(e.target.value)}
                        />
                    </InputGroup>
                    <Form.Select 
                        className="w-25"
                        value={category}
                        onChange={(e) => setCategory(e.target.value)}
                    >
                        <option value="">All Categories</option>
                        <option value="email">Email</option>
                        <option value="sms">SMS</option>
                    </Form.Select>
                </div>

                {loading ? (
                    <div className="text-center py-5">
                        <Spinner animation="border" variant="primary" />
                    </div>
                ) : (
                    <div className="table-responsive">
                        <Table hover>
                            <thead>
                                <tr>
                                    <th>Name</th>
                                    <th>Type</th>
                                    <th>Category</th>
                                    <th>Last Used</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredTemplates.map(template => (
                                    <tr key={template.id}>
                                        <td>{template.name}</td>
                                        <td>
                                            <span className={`badge bg-${template.type === 'both' ? 'primary' : template.type === 'email' ? 'success' : 'info'}`}>
                                                {template.type}
                                            </span>
                                        </td>
                                        <td>{template.category}</td>
                                        <td>{template.lastUsed ? new Date(template.lastUsed).toLocaleDateString() : 'Never'}</td>
                                        <td>
                                            <div className="d-flex gap-2">
                                                <Button 
                                                    variant="outline-primary" 
                                                    size="sm"
                                                    className="btn-icon"
                                                    onClick={() => handleEdit(template)}
                                                    title="Edit"
                                                >
                                                    <i className="fas fa-edit"></i>
                                                </Button>
                                                <Button 
                                                    variant="outline-danger" 
                                                    size="sm"
                                                    className="btn-icon"
                                                    onClick={() => handleDelete(template.id)}
                                                    title="Delete"
                                                >
                                                    <i className="fas fa-trash"></i>
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                )}
                {showEditModal && selectedTemplate && (
                    <AddTemplate 
                        template={selectedTemplate}
                        show={showEditModal}
                        onHide={() => {
                            setShowEditModal(false);
                            setSelectedTemplate(null);
                        }}
                        onSuccess={() => {
                            fetchTemplates();
                            setShowEditModal(false);
                            setSelectedTemplate(null);
                        }}
                        showNotification={showNotification}
                    />
                )}
            </Card.Body>
        </Card>
    );
};

export default TemplateList;